import { inject, Injectable } from '@angular/core';
import { NotificationService, NotificationServiceInjector } from '@next/core-lib/notification';
import { filter, map, tap } from 'rxjs';

import { FmsSyncEvents, FmsSyncStatusUpdatedEvent } from '../models/fms-sync.events';

@Injectable({ providedIn: 'root' })
export class FmsSyncNotificationService {
  private readonly notificationService = inject<NotificationService>(NotificationServiceInjector);

  // Set to track completed syncs and prevent racing conditions where 'Initiated' events
  // might arrive after 'Completed' or 'Failed' events for the same task.
  private readonly completedSyncs = new Set<string>();

  private readonly entry = this.notificationService
    .on<FmsSyncStatusUpdatedEvent | string>(FmsSyncEvents.statusUpdated)
    .pipe(
      map((event) => {
        if (typeof event !== 'string') {
          return event as FmsSyncStatusUpdatedEvent;
        }
        try {
          return JSON.parse(event) as FmsSyncStatusUpdatedEvent;
        } catch {
          return null;
        }
      }),
      filter((event): event is FmsSyncStatusUpdatedEvent => event !== null),
    );

  public readonly onInitiated = this.entry.pipe(
    filter((event) => event.SyncStatus === 'Initiated' && !this.completedSyncs.has(event.SyncTaskCode)),
  );

  public readonly onSyncing = this.entry.pipe(
    filter((event) => event.SyncStatus === 'Syncing' && !this.completedSyncs.has(event.SyncTaskCode)),
  );

  public readonly onSynced = this.entry.pipe(
    filter((event) => event.SyncStatus === 'Synced'),
    tap((event) => this.completedSyncs.add(event.SyncTaskCode)),
  );

  public readonly onFailed = this.entry.pipe(
    filter((event) => event.SyncStatus === 'Failed'),
    tap((event) => this.completedSyncs.add(event.SyncTaskCode)),
  );

  public readonly onNothingToSync = this.entry.pipe(
    filter((event) => event.SyncStatus === 'NothingToSync'),
    tap((event) => this.completedSyncs.add(event.SyncTaskCode)),
  );
}
